import { useState, useEffect, useRef, useMemo } from 'react';
import { Search, ChevronLeft, ChevronRight } from 'lucide-react';
import { useWizardProviderSearch, useServicesLight } from '@/api/usePresentationWizard';
import { Select } from '@/components/forms';
import { ProviderCard, ProviderDetailModal } from '@/components/providers';
import { useModalStore } from '@/stores';
import type { ProviderSummary } from '@/types/wizard';
import { fromProviderSummary } from '@/types/provider-card';
import { SelectedProviderChip, StepShell } from '../components';
import styles from './SelectProviders.module.css';

const PAGE_SIZE = 5;

const RATING_OPTIONS = [
  { value: '', label: 'Any rating' },
  { value: '3', label: '3+ stars' },
  { value: '4', label: '4+ stars' },
  { value: '5', label: '5 stars' },
];

const CURRENCY_OPTIONS = [
  { value: 'PEN', label: 'S/. Soles (PEN)' },
  { value: 'USD', label: '$ Dollars (USD)' },
];

/** Cities where USD is the natural default. */
const USD_CITIES = new Set([
  'Miami',
  'New York',
  'Los Angeles',
  'Houston',
  'Chicago',
  'San Francisco',
  'Washington',
  'Dallas',
  'Orlando',
  'San Diego',
]);

interface Props {
  selectedProviders: ProviderSummary[];
  onProvidersChange: (providers: ProviderSummary[]) => void;
  onNext: () => void;
  city: string;
}

export const SelectProviders = ({
  selectedProviders,
  onProvidersChange,
  onNext,
  city,
}: Props) => {
  const [rawSearch, setRawSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [minRating, setMinRating] = useState('');
  const [serviceSlug, setServiceSlug] = useState('');
  const [serviceInput, setServiceInput] = useState('');
  const [cityValue, setCityValue] = useState(city);
  const [currency, setCurrency] = useState(() => (USD_CITIES.has(city) ? 'USD' : 'PEN'));
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const { openModal } = useModalStore();
  const { data: servicesData } = useServicesLight();

  // Keep cityValue in sync with the city prop from the wizard
  useEffect(() => {
    setCityValue(city);
    setCurrency(USD_CITIES.has(city) ? 'USD' : 'PEN');
  }, [city]);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setDebouncedSearch(rawSearch.trim());
    }, 300);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [rawSearch]);

  const filters = useMemo(() => {
    const f: Record<string, string | number> = {};
    if (minRating) f.min_rating = Number(minRating);
    if (serviceSlug) f.service = serviceSlug;
    if (cityValue) f.city = cityValue;
    if (currency) f.currency = currency;
    return Object.keys(f).length > 0 ? f : undefined;
  }, [minRating, serviceSlug, cityValue, currency]);

  const { data, isFetching } = useWizardProviderSearch(debouncedSearch, filters);
  const results = data?.results ?? [];
  const similar = data?.similar ?? [];

  const serviceOptions = useMemo(() => {
    const opts = [{ value: '', label: 'All services' }];
    if (servicesData) {
      for (const s of servicesData) {
        opts.push({ value: s.slug, label: s.name });
      }
    }
    return opts;
  }, [servicesData]);

  const selectedIds = new Set(selectedProviders.map((p) => p.id));

  // Pagination state for each group
  const [resultsPage, setResultsPage] = useState(0);
  const [similarPage, setSimilarPage] = useState(0);

  // Reset pages when data changes
  useEffect(() => {
    setResultsPage(0);
  }, [results.length]);
  useEffect(() => {
    setSimilarPage(0);
  }, [similar.length]);

  const toggleProvider = (provider: ProviderSummary) => {
    if (selectedIds.has(provider.id)) {
      onProvidersChange(selectedProviders.filter((p) => p.id !== provider.id));
    } else {
      onProvidersChange([...selectedProviders, provider]);
    }
  };

  const removeProvider = (id: string) => {
    onProvidersChange(selectedProviders.filter((p) => p.id !== id));
  };

  const viewProviderDetails = (provider: ProviderSummary) => {
    openModal(
      <ProviderDetailModal
        provider={fromProviderSummary(provider)}
        isSelected={selectedIds.has(provider.id)}
        onToggle={() => toggleProvider(provider)}
      />,
    );
  };

  const hasActiveFilters = Boolean(
    debouncedSearch || serviceSlug || minRating || currency !== 'PEN',
  );

  const nextLabel =
    selectedProviders.length > 0
      ? `Continue with ${selectedProviders.length} provider${selectedProviders.length !== 1 ? 's' : ''}`
      : 'Continue without providers';

  return (
    <StepShell
      title="Select Providers"
      subtitle="Add providers to include in the presentation"
      nextLabel={nextLabel}
      onNext={onNext}
    >
      <div className={styles.inner}>
        <p className={styles.optional}>
          This step is optional &mdash; you can continue without selecting providers
        </p>

        {/* Search & filter row */}
        <div className={styles.filtersSection}>
          <div className={styles.searchWrapper}>
            <Search size={16} className={styles.searchIcon} />
            <input
              className={styles.searchInput}
              type="text"
              value={rawSearch}
              onChange={(e) => setRawSearch(e.target.value)}
              placeholder="Search providers by name..."
            />
          </div>

          <div className={styles.filtersRow}>
            <div className={styles.filterItem}>
              <Select
                value={serviceSlug}
                onChange={(e) => setServiceSlug(e.target.value)}
                options={serviceOptions}
                placeholder="All services"
                size="md"
                allowInput
                inputValue={serviceInput}
                onInputChange={setServiceInput}
              />
            </div>
            <div className={styles.filterItemSmall}>
              <Select
                value={minRating}
                onChange={(e) => setMinRating(e.target.value)}
                options={RATING_OPTIONS}
                size="md"
              />
            </div>
            <div className={styles.filterItemSmall}>
              <Select
                value={currency}
                onChange={(e) => setCurrency(e.target.value)}
                options={CURRENCY_OPTIONS}
                size="md"
              />
            </div>
          </div>
        </div>

        {/* Selected chips */}
        {selectedProviders.length > 0 && (
          <div className={styles.selectedSection}>
            <span className={styles.selectedLabel}>Selected ({selectedProviders.length}):</span>
            <div className={styles.chipsRow}>
              {selectedProviders.map((p) => (
                <SelectedProviderChip
                  key={p.id}
                  name={p.nombre_comercial}
                  onRemove={() => removeProvider(p.id)}
                />
              ))}
            </div>
          </div>
        )}

        {/* Results section */}
        <div className={styles.resultsSection}>
          {isFetching ? (
            <p className={styles.hint}>Searching...</p>
          ) : (
            <>
              {/* Main results */}
              {results.length > 0 && (
                <div className={styles.resultGroup}>
                  <div className={styles.groupHeader}>
                    <h3 className={styles.groupTitle}>
                      {hasActiveFilters ? 'Matching providers' : 'Providers'}
                      <span className={styles.groupCount}>{results.length}</span>
                    </h3>
                    {results.length > PAGE_SIZE && (
                      <div className={styles.pagination}>
                        <button
                          className={styles.pageArrow}
                          onClick={() => setResultsPage((p) => p - 1)}
                          disabled={resultsPage === 0}
                          aria-label="Previous page"
                        >
                          <ChevronLeft size={18} />
                        </button>
                        <span className={styles.pageIndicator}>
                          {resultsPage + 1} / {Math.ceil(results.length / PAGE_SIZE)}
                        </span>
                        <button
                          className={styles.pageArrow}
                          onClick={() => setResultsPage((p) => p + 1)}
                          disabled={resultsPage >= Math.ceil(results.length / PAGE_SIZE) - 1}
                          aria-label="Next page"
                        >
                          <ChevronRight size={18} />
                        </button>
                      </div>
                    )}
                  </div>
                  <div className={styles.grid}>
                    {results
                      .slice(resultsPage * PAGE_SIZE, (resultsPage + 1) * PAGE_SIZE)
                      .map((provider) => (
                        <ProviderCard
                          key={provider.id}
                          provider={fromProviderSummary(provider)}
                          isSelected={selectedIds.has(provider.id)}
                          onToggle={() => toggleProvider(provider)}
                          onViewDetails={() => viewProviderDetails(provider)}
                        />
                      ))}
                  </div>
                </div>
              )}

              {/* Similar / suggested providers */}
              {similar.length > 0 && (
                <div className={styles.resultGroup}>
                  <div className={styles.groupHeader}>
                    <h3 className={styles.groupTitle}>
                      {results.length > 0
                        ? 'You might also consider'
                        : hasActiveFilters
                          ? 'Similar providers'
                          : 'Recent providers'}
                      <span className={styles.groupCount}>{similar.length}</span>
                    </h3>
                    {similar.length > PAGE_SIZE && (
                      <div className={styles.pagination}>
                        <button
                          className={styles.pageArrow}
                          onClick={() => setSimilarPage((p) => p - 1)}
                          disabled={similarPage === 0}
                          aria-label="Previous page"
                        >
                          <ChevronLeft size={18} />
                        </button>
                        <span className={styles.pageIndicator}>
                          {similarPage + 1} / {Math.ceil(similar.length / PAGE_SIZE)}
                        </span>
                        <button
                          className={styles.pageArrow}
                          onClick={() => setSimilarPage((p) => p + 1)}
                          disabled={similarPage >= Math.ceil(similar.length / PAGE_SIZE) - 1}
                          aria-label="Next page"
                        >
                          <ChevronRight size={18} />
                        </button>
                      </div>
                    )}
                  </div>
                  <div className={styles.grid}>
                    {similar
                      .slice(similarPage * PAGE_SIZE, (similarPage + 1) * PAGE_SIZE)
                      .map((provider) => (
                        <ProviderCard
                          key={provider.id}
                          provider={fromProviderSummary(provider)}
                          isSelected={selectedIds.has(provider.id)}
                          onToggle={() => toggleProvider(provider)}
                          onViewDetails={() => viewProviderDetails(provider)}
                        />
                      ))}
                  </div>
                </div>
              )}

              {results.length === 0 && similar.length === 0 && (
                <p className={styles.hint}>No providers found</p>
              )}
            </>
          )}
        </div>
      </div>
    </StepShell>
  );
};
